import { FilterCondition } from 'framework/types';
import { useCollectionPager } from 'framework/ViewTypes/hooks';
import { DESC } from 'framework/list';
import { useTechnologyService } from 'domain/shared/career';
import { usePositionService } from 'domain/shared/employee';
import { DEFAULT_POSITION_PROFILE_STATUSES, POSITION_PROFILE_STATUSES } from '../constants';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import type { PositionProfileListRow, PositionProfilesFilters } from '../types';
import usePositionProfilesService from '../usePositionProfilesService';
import { mapPositionProfileListRows } from './positionProfileListMapper';
import { CollectionViewParamsConfig } from 'framework/ViewTypes';

const DEFAULT_POSITION_PROFILES_FILTERS: PositionProfilesFilters = {
  status: { [FilterCondition.IN]: DEFAULT_POSITION_PROFILE_STATUSES },
};

const POSITION_PROFILES_VIEW_PARAMS_CONFIG: CollectionViewParamsConfig<PositionProfilesFilters> = {
  defaultFilters: DEFAULT_POSITION_PROFILES_FILTERS,
  defaultSortBy: 'createdAt',
  defaultSortOrder: DESC,
  cache: true,
  filterRules: {
    specializationId: { condition: FilterCondition.IN },
    technologyId: { condition: FilterCondition.IN },
    competencyLevel: { condition: FilterCondition.IN, values: Object.keys(EMPLOYEE_COMPETENCY_LEVEL) },
    status: { condition: FilterCondition.IN, values: Object.values(POSITION_PROFILE_STATUSES) },
  },
  sortableColumns: ['createdAt', 'name', 'competencyLevel', 'status'],
};

const usePositionProfilesCollection = () => {
  const { search } = usePositionProfilesService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();

  return useCollectionPager<PositionProfileListRow, PositionProfilesFilters>({
    pageSize: 30,
    viewParamsConfig: POSITION_PROFILES_VIEW_PARAMS_CONFIG,
    fetchPage: async ({ pageNumber, pageSize, filters, searchValue, sort, signal }) => {
      const [response, positions, technologies] = await Promise.all([
        search(
          {
            pageNumber,
            pageSize,
            filter: {
              ...filters,
              ...(searchValue ? { name: { [FilterCondition.CT]: searchValue } } : {}),
            },
            sort,
          },
          { signal },
        ),
        findAllPositions().catch(() => []),
        findAllTechnologies().catch(() => []),
      ]);
      signal?.throwIfAborted();

      return {
        ...response,
        result: mapPositionProfileListRows(response.result, positions, technologies),
      };
    },
  });
};

export default usePositionProfilesCollection;
